import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, UpdateOrderRequestBody, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import {
  HrailSupport,
  InsertHrailSupportBody,
  UpdateHrailSupportBody,
} from "./types/supports";

export const useHrailSupportsApi = () => {
  const api = useApi();

  const getAdminHrailSupport = useCallback(
    (supportId: string, signal: AbortSignal) => {
      const url = `/admin/settings/hrail/supports/${supportId}`;
      return useSWRMutation<HrailSupport, CustomError, string>(url, (url) =>
        api.get<HrailSupport>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminHrailSupports = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/hrail/supports`, query);
      return useSWRImmutable<Paginated<HrailSupport>, CustomError, string>(url, (url) =>
        api.get<Paginated<HrailSupport>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminHrailSupports = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/hrail/supports`, query);
      return api.get<Paginated<HrailSupport>>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertAdminHrailSupport = useCallback(
    async (body: InsertHrailSupportBody): Promise<HrailSupport> => {
      const item = await api.post<HrailSupport, InsertHrailSupportBody>(
        `/admin/settings/hrail/supports`,
        body,
      );
      mutateCache([`/admin/settings/hrail/supports`]);
      return item;
    },
    [api],
  );

  const updateAdminHrailSupport = useCallback(
    async (supportId: string, body: UpdateHrailSupportBody): Promise<void> => {
      const url = `/admin/settings/hrail/supports/${supportId}`;
      await api.put<UpdateHrailSupportBody>(url, body);
      mutateCache([`/admin/settings/hrail/supports`], url);
    },
    [api],
  );

  const updateAdminHrailSupportVisibility = useCallback(
    async (supportId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/hrail/supports/${supportId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache([`/admin/settings/hrail/supports`]);
    },
    [api],
  );

  const updateAdminHrailSupportOrder = useCallback(
    async (supportId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/hrail/supports/${supportId}/order`;
      await api.put<UpdateOrderRequestBody>(url, order);
      mutateCache([`/admin/settings/hrail/supports`]);
    },
    [api],
  );

  const deleteAdminHrailSupport = useCallback(
    async (supportId: string): Promise<void> => {
      await api.del(`/admin/settings/hrail/supports/${supportId}`);
      mutateCache([`/admin/settings/hrail/supports`]);
    },
    [api],
  );

  return {
    getAdminHrailSupport,
    useAdminHrailSupports,
    getAdminHrailSupports,
    insertAdminHrailSupport,
    updateAdminHrailSupport,
    updateAdminHrailSupportVisibility,
    updateAdminHrailSupportOrder,
    deleteAdminHrailSupport,
  };
};
